// @flow
/**
 * This component acts as a UI for editing a single prop of a component. In
 * other words, given a component <Button url="/hom" text="Click Me" />, there
 * would be two instances of this component in the editor, one for each prop.
 *
 * Props may also be arrays, and the array items themselves may have arrays
 * infinitely, so this component is recursive to support that. However, we
 * should generally strive to write components that have as little nesting as
 * possible.
 */
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import moment from 'moment';
import { SketchPicker } from 'react-color';
import TextInput from '~/app/components/TextInput';
import TextArea from '~/app/components/TextArea';
import {
  COMPONENT_VALUE_TYPES as types,
  S3_ASSET_BROWSER_DEFAULT_OPTIONS
} from 'shared/constants';
import IconPicker from './IconPicker';
import Checkbox from './Checkbox';
import Dropdown from './Dropdown';
import CalendarPicker from './CalendarPicker';
import DateTimePicker from './DateTimePicker';
import GalleryEditor from './GalleryEditor';
import TextEditor from './TextEditor';
import FutureSightConnect from './FutureSightConnect';
import FlexibleColumnLayout from './FlexibleColumnLayout';
import BackgroundPicker from './BackgroundPicker';
import S3AssetBrowser from '~/pages/components/S3AssetBrowser';
import MoveToPosition from './MoveToPosition';
import PlaylistJsonUploader from '~/pages/components/S3AssetBrowser/PlaylistJsonUploader';
import LivestreamSceneControl from '~/pages/components/S3AssetBrowser/LivestreamSceneControl';
import PrimaryTooltip from '@ff/ui/Tooltip/presets/PrimaryTooltip';
import EditComponentStickyHeader from './EditComponentStickyHeader';
import EditComponentContext from './editComponentContext';
import { S, A, TONES, DARK } from './shell/shellTokens';
import loadable from '@loadable/component';
import {
  isEnumPropSchema,
  isEnumOptionsList,
  isDateTimePropSchema,
  resolveEnumOptionsForSchema,
} from 'shared/socialIconEnumOptions';

const TinyMCETextEditor = loadable(() => import(/* webpackChunkName: "TinyMCETextEditor" */ './TinyMCETextEditor'));
const CodeEditor = loadable(() => import(/* webpackChunkName: "CodeEditor" */ './CodeEditor'))
const RichTextEditor = loadable(() => import(/* webpackChunkName: "RichTextEditor" */ './RichTextEditor'))
const AiCodeGeneratorEditor = loadable(() => import(/* webpackChunkName: "AiCodeGeneratorEditor" */ './AiCodeGeneratorEditor'));

const enumDefaultValue = (options) => {
  if (!options || !options.length) return '';
  const first = options[0];
  if (first && typeof first === 'object') {
    return first.value ?? first.label ?? '';
  }
  return String(first);
};

type Schema = {
  type: string,
  label: string,
  key: string,
  options: any[],
  children: ?Schema[]
}

type Props = {
  schema: Schema,
  classes: Object,
  value: any,
  onChange: Function,
  root: ?boolean,
  headerPrefix: string,
  headerDepth: number,
  hiddenIndices: ?number[],
  schemaParentKey: ?string
}

type Animate = {
  type: "Move Up" | "Move Down",
  index: Number
}

type AnimateDirection = "Up" | "Down";

type State = {
  animate: ?Animate,
  highlightedIndex: ?number,
  animationReady: boolean,
  hiddenIndices: number[]
}

// The inspector column is narrow: colors open their picker on demand.
function CompactColor({ value, onChange, classes }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={classes.compactColor}>
      <button type="button" className={classes.compactSwatchButton} onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className={classes.compactSwatch} style={{ background: value || 'transparent' }} />
        <span className={classes.compactColorValue}>{value || 'None'}</span>
      </button>
      {open && (
        <SketchPicker
          color={value}
          onChange={(color) => {
            onChange(`rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`);
          }}
        />
      )}
    </div>
  );
}

class EditComponentProp extends React.Component<Props, State> {
  props: Props = this.props;

  // `compact` is set by the inspector column (EditComponent's panel variant).
  static contextType = EditComponentContext;

  state: State = {
    animate: null,
    hiddenIndices: [],
    highlightedIndex: null,
    animationReady: false // Controls when the fade animation starts
  };

  // Ref map to store references to array item elements
  itemRefs: Map<number, HTMLElement> = new Map();

  toggleHidden = (i) => {
    this.setState(({ hiddenIndices }) => {
      // Remove selected index from the list
      if (hiddenIndices.includes(i)) {
        return ({
          hiddenIndices: hiddenIndices.filter(x => x !== i)
        })
      }

      // Add selected index to the list
      return ({
        hiddenIndices: [i, ...hiddenIndices]
      })
    });
  }

  isHidden = (i) => {
    return this.state.hiddenIndices.includes(i);
  }

  // Scroll to an item and trigger highlight animation after scroll completes
  scrollToItemAndHighlight = (index: number) => {
    // Set the highlight state immediately (shows static highlight)
    this.setState({ highlightedIndex: index, animationReady: false }, () => {
      // Wait for React to render, then scroll
      requestAnimationFrame(() => {
        const element = this.itemRefs.get(index);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // After scroll completes (estimate ~400ms for smooth scroll), start the fade animation
          setTimeout(() => {
            this.setState({ animationReady: true });
            // Clear highlight after animation completes
            setTimeout(() => this.setState({ highlightedIndex: null, animationReady: false }), 600);
          }, 400);
        } else {
          // Fallback if ref not found - just do the animation
          this.setState({ animationReady: true });
          setTimeout(() => this.setState({ highlightedIndex: null, animationReady: false }), 600);
        }
      });
    });
  }

  // Remove the item at 'fromIndex', and insert at 'toIndex'
  // Ex: [A, B, C, D]; moveItem(3, 2) => [A, B, D, C]
  // Ex: [A, B, C, D]; moveItem(2, 3) => [A, B, D, C]
  // Ex: [A, B, C, D]; moveItem(0, 3) => [B, C, D, A]
  moveItem = (fromIndex, toIndex, skipHighlight = false) => {
    const { value, schema, onChange } = this.props;

    if (fromIndex !== toIndex && toIndex >= 0 && toIndex < value.length) {
      const res = value.slice();

      const item = res.splice(fromIndex, 1)[0];

      res.splice(toIndex, 0, item);

      onChange(schema.key, res);

      // After moving, each item should retain its 'hidden' state
      if (this.isHidden(fromIndex) !== this.isHidden(toIndex)) {
        this.toggleHidden(fromIndex);
        this.toggleHidden(toIndex);
      }

      // Scroll to and highlight the moved item at its new position
      if (!skipHighlight) {
        this.scrollToItemAndHighlight(toIndex);
      }
    }
  }

  doAnimationAndMoveItem = (index, direction: AnimateDirection) => {
    const isUp = direction === 'Up';

    this.setState({ animate: { type: isUp ? 'Move Up' : 'Move Down', index }},
      () => {
        setTimeout(() => {
          const newIndex = isUp ? index - 1 : index + 1;

          this.moveItem(index, newIndex, true); // Skip highlight during move, we'll add it after

          this.setState({ animate: null });
          // For single-step moves (up/down), the item is likely still visible, 
          // so just scroll and highlight
          this.scrollToItemAndHighlight(newIndex);
        }, 300);
      });
  }

  componentDidUpdate = (prevProps) => {
    if (this.props.hiddenIndices && this.props.hiddenIndices !== prevProps.hiddenIndices) {
      this.setState({ hiddenIndices: this.props.hiddenIndices });
    }
  }

  render() {
    const {
      classes,
      schema,
      onChange,
      root,
      schemaParentKey
    } = this.props;

    let { value } = this.props;
    const { animate } = this.state;

    // Set empty defaults in case of undefined value
    if (!value) {
      if (schema.type === 'ARRAY') {
        value = [];
      } else if (schema.type === 'OBJECT') {
        value = {};
      } else if (isDateTimePropSchema(schema)) {
        value = {};
      }
    } else if (
      isDateTimePropSchema(schema) &&
      typeof value !== 'object'
    ) {
      value = {};
    } else {
      // In case of schema change
      if (schema.type === 'ARRAY' && !Array.isArray(value)) {
        value = [];
      }
    }

    const hasChildren: boolean = Array.isArray(schema.children) &&
      schema.children.length > 0;
    const compact = Boolean(this.context && this.context.compact);
    const ci = classNames(classes.commonInput, compact && classes.commonInputCompact);
    // In the inspector, a group's header is a small toggle (the sticky header is sized for the edit window).
    const header = (text, i) => (compact
      ? (
        <button type="button" className={classes.compactHeader} onClick={() => this.toggleHidden(i)} aria-expanded={!this.isHidden(i)}>
          <span>{text}</span>
          <i className="material-icons" aria-hidden>{this.isHidden(i) ? 'expand_more' : 'expand_less'}</i>
        </button>
      )
      : null);
    const label = (
      <div style={{ display: 'flex' }}>
        <div
          className={classNames(root ? classes.title : classes.label, compact && (root ? classes.compactRootLabel : classes.compactLabel))}
        >
          {schema.label}
        </div>
        {
          schema.tooltip &&
            <PrimaryTooltip
              toolTipPosition='right'
              minWidth={{ _: '180px', md: '280px' }}
            >
              {schema.tooltip}
            </PrimaryTooltip>
        }
      </div>
    );

    const formattedHeaderPrefix = this.props.headerPrefix ? `${this.props.headerPrefix} » ` : '';

    const { highlightedIndex, animationReady } = this.state;

    const arrayItems = hasChildren && schema.type === 'ARRAY' && (
      value && value.map((item, i) => (
        <div 
          ref={(el) => {
            if (el) this.itemRefs.set(i, el);
            else this.itemRefs.delete(i);
          }}
          className={classNames(classes.children, compact && classes.childrenCompact, { 
            [classes.highlightedItemStatic]: highlightedIndex === i && !animationReady,
            [classes.highlightedItemFading]: highlightedIndex === i && animationReady
          })} 
          key={i}
        >
          {compact ? header(`${schema.label}: ${i + 1}`, i) : (
            <EditComponentStickyHeader
              headerDepth={this.props.headerDepth}
              headerPrefix={formattedHeaderPrefix}
              label={`${schema.label}: ${i + 1}`}
              isHidden={this.isHidden(i)}
              toggleHidden={() => this.toggleHidden(i)}
            />
          )}
          {
            !this.isHidden(i) && (
              <>
                {schema.children && schema.children.map(child => (
                  <EditComponentProp
                    root={false}
                    headerPrefix={`${formattedHeaderPrefix}${schema.label}: ${i + 1}`}
                    headerDepth={(this.props.headerDepth || 0) + 1}
                    schemaParentKey={schema.key}
                    key={child.key}
                    schema={child}
                    classes={classes}
                    onChange={(key, newVal) => {
                      const res = value.slice();

                      res[i] = {
                        ...res[i],
                        [key]: newVal
                      };
                      onChange(schema.key, res);
                    }}
                    value={item[child.key]}
                  />
                ))}
                {

                  /* Hide "Move Up" for first item */
                  i !== 0 && (
                    <div
                      className={classes.moveItem}
                    >
                      <div
                        className={animate && animate.index === i && animate.type === 'Move Up' ?
                          classNames(classes.moveBtn, classes.animateMoveItem) : classes.moveBtn}
                        onClick={() => this.doAnimationAndMoveItem(i, 'Up')}
                      >
                        <i className="material-icons">arrow_upward</i>
                        {`Move up ${schema.label}`}
                      </div>

                    </div>
                  )
                }
                {
                  value.length > 1 && (
                    <div className={classes.moveItem}>
                      <div className={classes.moveBtn}>
                        <MoveToPosition onMoveItem={toIndex => this.moveItem(i, toIndex)} />
                      </div>
                    </div>
                  )
                }
                {

                  /* Hide "Move Down" for last item */
                  i < (value.length - 1) && (
                    <div
                      className={classes.moveItem}
                    >
                      <div
                        className={animate && animate.index === i && animate.type === 'Move Down' ?
                          classNames(classes.moveBtn, classes.animateMoveItem) : classes.moveBtn}
                        onClick={() => this.doAnimationAndMoveItem(i, 'Down')}
                      >
                        <i className="material-icons">arrow_downward</i>
                        {`Move Down ${schema.label}`}
                      </div>
                    </div>
                  )
                }
                <div
                  className={classes.removeItem}
                >
                  <div
                    className={classes.removeBtn}
                    onClick={() => {
                      const res = value.slice();

                      res.splice(i, 1);
                      onChange(schema.key, res);
                    }}
                  >
                    <i className="material-icons">remove_circle_outline</i>
                    {`Remove ${schema.label}`}
                  </div>
                </div>
              </>
            )
          }
        </div>
      ))
    );
    // Compare child count to max length, if applicable
    const hasChildCountBelowMax = !(schema.type === 'ARRAY' && schema?.options?.maxLength && parseInt(schema?.options?.maxLength) <= value?.length);
    const addItem = hasChildren && schema.type === 'ARRAY' && hasChildCountBelowMax && (
      <div
        className={classes.addItem}
        onClick={() => {
          const res = value.slice();
          const obj = {};

          if (schema.children) {
            schema.children.forEach((child) => {
              // TODO: This implementation assumes a text input.
              if (child.type === 'ARRAY') {
                obj[child.key] = [];
              } else if (isEnumPropSchema(child)) {
                obj[child.key] = enumDefaultValue(resolveEnumOptionsForSchema(child, schema.key));
              } else if (child.type === 'DATE') {
                obj[child.key] = moment().utc().format();
              } else if (child.type === 'OBJECT') {
                const cObj = {};
                const objectField = child;

                (objectField.children || []).forEach((nestedChild) => {
                  // TODO: This implementation assumes a text input.
                  if (nestedChild.type === 'ARRAY') {
                    cObj[nestedChild.key] = [];
                  } else if (isEnumPropSchema(nestedChild)) {
                    cObj[nestedChild.key] = enumDefaultValue(
                      resolveEnumOptionsForSchema(nestedChild, objectField.key),
                    );
                  } else if (nestedChild.type === 'DATE') {
                    cObj[nestedChild.key] = moment().utc().format();
                  } else if (nestedChild.type === 'OBJECT') {
                    cObj[nestedChild.key] = {};
                  } else {
                    cObj[nestedChild.key] = '';
                  }
                });
                obj[objectField.key] = cObj;
              } else {
                obj[child.key] = '';
              }

            });
          }
          res.push(obj);
          onChange(schema.key, res);
        }}
      >
        <i className="material-icons">add_circle</i>
        {`Add ${schema.label}`}
      </div>
    );

    // Simple string array items (arrays without children - just strings)
    const simpleArrayItems = !hasChildren && schema.type === 'ARRAY' && (
      value && value.map((item, i) => (
        <div className={classes.simpleArrayItem} key={i}>
          <div className={classes.simpleArrayItemHeader}>
            <span className={classes.simpleArrayItemLabel}>{`${schema.label} ${i + 1}`}</span>
          </div>
          <div className={classes.simpleArrayItemContent}>
            <TextInput
              type="text"
              ariaLabel={`${schema.label} ${i + 1}`}
              className={classes.simpleArrayInput}
              value={
                typeof item === 'string' ? item :
                typeof item === 'object' && item !== null ? (
                  item.value ?? item.text ?? item.name ?? item.label ?? 
                  (Object.keys(item).filter(k => !k.startsWith('_')).length === 1 
                    ? item[Object.keys(item).filter(k => !k.startsWith('_'))[0]] 
                    : '')
                ) : String(item ?? '')
              }
              onChange={event => {
                const res = value.slice();
                res[i] = event.target.value; // Always save as string
                onChange(schema.key, res);
              }}
            />
            <div
              className={classes.simpleArrayRemoveBtn}
              onClick={() => {
                const res = value.slice();
                res.splice(i, 1);
                onChange(schema.key, res);
              }}
            >
              <i className="material-icons">close</i>
            </div>
          </div>
        </div>
      ))
    );

    // Add button for simple string arrays
    const addSimpleItem = !hasChildren && schema.type === 'ARRAY' && hasChildCountBelowMax && (
      <div
        className={classes.addItem}
        onClick={() => {
          const res = value ? value.slice() : [];
          res.push(''); // Add empty string for simple arrays
          onChange(schema.key, res);
        }}
      >
        <i className="material-icons">add_circle</i>
        {`Add ${schema.label}`}
      </div>
    );

    const objectFields = hasChildren && schema.type === 'OBJECT' && (
      <div className={classNames(classes.children, compact && (root ? classes.childrenFlat : classes.childrenCompact))}>
        {compact ? (!root && header(schema.label, 0)) : (
          <EditComponentStickyHeader
            headerDepth={this.props.headerDepth}
            headerPrefix={formattedHeaderPrefix}
            label={schema.label}
            isHidden={this.isHidden(0)}
            toggleHidden={() => this.toggleHidden(0)}
          />
        )}
        {!this.isHidden(0) && schema.children && schema.children.map(fieldSchema => (
          <EditComponentProp
            root={false}
            headerPrefix={`${formattedHeaderPrefix}${schema.label}`}
            headerDepth={(this.props.headerDepth || 0) + 1}
            schemaParentKey={schema.key}
            key={fieldSchema.key}
            schema={fieldSchema}
            classes={classes}
            onChange={(key, newVal) => {
              let res = value;

              res = {
                ...value,
                [fieldSchema.key]: newVal
              };
              onChange(schema.key, res);
            }}
            value={value[fieldSchema.key]}
          />
        ))}
      </div>
    );

    return (
      <div
        className={classNames(classes.schema, {
          [classes.inline]: schema.type === 'TEXT',
          'is-child': !root
        })}
      >
        {
          /* Show label except for OBJECT, BOOLEAN, and ARRAY with children (nested arrays show their own headers) */
          !(
            schema.type === 'OBJECT' ||
            schema.type === 'BOOLEAN' ||
            (schema.type === 'ARRAY' && hasChildren)
          ) && !(compact && root) && label
        }
        {arrayItems}
        {addItem}
        {simpleArrayItems}
        {addSimpleItem}
        {objectFields}
        {schema.type === 'WATERFALL_GALLERY' &&
          <GalleryEditor
            value={value}
            className={ci}
            onChange={val => onChange(schema.key, val)}
          />
        }
        {schema.type === 'FUTURESIGHT_CONNECT' &&
          <FutureSightConnect
            value={value}
            className={ci}
            onChange={val => onChange(schema.key, val)}
          />
        }
        {schema.type === 'TEXT_WITH_ICON' &&
          <TextEditor
            type="text"
            className={classes.textInput}
            value={value}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && schema.type === 'TEXT' && !isEnumOptionsList(schema.options) &&
          <TextInput
            key={schema.key}
            type="text"
            className={classes.textInput}
            ariaLabel={schema.label}
            value={value}
            onChange={event => onChange(schema.key, event.target.value)}
            options={schema?.options}
            compact={compact}
          />
        }
        {!hasChildren && schema.type === 'TEXTAREA' &&
          <TextArea
            type="textarea"
            className={classes.textInput}
            ariaLabel={schema.label}
            value={value}
            onChange={event => onChange(schema.key, event.target.value)}
            options={schema?.options}
          />
        }
        {!hasChildren && schema.type === 'RICHTEXT' &&
          <RichTextEditor
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && schema.type === 'TINYMCE' &&
          <TinyMCETextEditor
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
            options={schema?.options}
          />
        }
        {!hasChildren && schema.type === 'HTML' &&
          <CodeEditor
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && schema.type === 'ICON' &&
          <IconPicker
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && schema.type === 'ICON_SOCIAL' &&
          <IconPicker
            socialIcons
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && schema.type === 'BOOLEAN' &&
          <Checkbox
            label={schema.label}
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && isEnumPropSchema(schema) &&
          <Dropdown
            ariaLabel={schema.label}
            value={value}
            options={resolveEnumOptionsForSchema(schema, schemaParentKey)}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && schema.type === 'DATE' &&
          <CalendarPicker
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && isDateTimePropSchema(schema) &&
          <DateTimePicker
            value={value}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {!hasChildren && schema.type === 'COLOR_PICKER' && compact &&
          <CompactColor value={value} classes={classes} onChange={newVal => onChange(schema.key, newVal)} />
        }
        {!hasChildren && schema.type === 'COLOR_PICKER' && !compact &&
          <SketchPicker
            color={value}
            className={classNames(classes.commonInput, classes.sketchPicker)}
            onChange={(color) => {
              // Convert color rgb object into a string
              const rgbaString = `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`;
              onChange(schema.key, rgbaString);
            }}
          />
        }
        {schema.type === 'FLEXIBLE_COLUMN_LAYOUT' &&
          <FlexibleColumnLayout
            value={value}
            schema={schema}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {schema.type === 'AI_CODE_GENERATOR' &&
          <AiCodeGeneratorEditor
            value={value}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {schema.type === 'BACKGROUND_PICKER' &&
          <BackgroundPicker
            value={value}
            className={ci}
            onChange={newVal => onChange(schema.key, newVal)}
          />
        }
        {schema.type === types.S3_ASSET_URL &&
          <S3AssetBrowser
            value={value}
            className={ci}
            onChange={newValue => onChange(schema.key, newValue)}
            options={{ ...S3_ASSET_BROWSER_DEFAULT_OPTIONS, ...schema.options }}
          />
        }
        {schema.type === types.LIVESTREAM_PLAYLIST &&
          <PlaylistJsonUploader
            // wrapper for another EditComponentProp pass through props directly
            {...this.props}
          />
        }
        {schema.type === types.LIVESTREAM_CONTROL &&
          <LivestreamSceneControl
            // simple in-widget control: writes control.json to S3 to switch media live
            {...this.props}
          />
        }
        {schema.type === types.INFO &&
          <div className={classes.infoBox} style={{ borderRadius: 6, padding: '14px 18px', fontSize: 13, lineHeight: 1.6, maxWidth: 720 }}>
            {(() => {
              const raw = (schema.defaultValue !== undefined && schema.defaultValue !== null && schema.defaultValue !== '')
                ? schema.defaultValue
                : value;
              const lines = Array.isArray(raw) ? raw : String(raw || '').split('\n');
              return lines.map((line, i) => {
                const t = String(line).trim();
                if (!t) return <div key={i} style={{ height: 8 }} />;
                if (/^(\d+\.|[-•])\s/.test(t)) return <div key={i} style={{ paddingLeft: 16, margin: '3px 0' }}>{t}</div>;
                if (t.endsWith(':')) return <div key={i} style={{ fontWeight: 600, margin: '12px 0 2px' }}>{t}</div>;
                return <div key={i} style={{ margin: '3px 0' }}>{t}</div>;
              });
            })()}
          </div>
        }
      </div>
    );
  }
}

const styles = {
  '@keyframes buttonClick': {
    '0%': { color: 'hsl(0, 0%, 40%)' },
    '50%': { color: 'hsl(0, 70%, 40%)' },
    '100%': { color: 'hsl(0, 0%, 40%)' }
  },
  '@keyframes buttonClickDark': {
    '0%': { color: S.muted },
    '50%': { color: S.red },
    '100%': { color: S.muted }
  },
  '@keyframes highlightFadeOut': {
    '0%': {
      backgroundColor: 'hsl(210, 100%, 90%)',
      boxShadow: '0 0 0 3px hsl(210, 100%, 50%)',
      transform: 'scale(1.008)'
    },
    '100%': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      transform: 'scale(1)'
    }
  },
  '@keyframes highlightFadeOutDark': {
    '0%': {
      backgroundColor: A.rowSelected,
      boxShadow: `0 0 0 3px ${S.blue}`,
      transform: 'scale(1.008)'
    },
    '100%': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      transform: 'scale(1)'
    }
  },
  animateMoveItem: {
    animation: '$buttonClick 300ms',
    [DARK]: { animation: '$buttonClickDark 300ms' }
  },
  // Static highlight - shows immediately while scrolling
  highlightedItemStatic: {
    backgroundColor: 'hsl(210, 100%, 90%)',
    boxShadow: '0 0 0 3px hsl(210, 100%, 50%)',
    transform: 'scale(1.008)',
    transition: 'none',
    [DARK]: { backgroundColor: A.rowSelected, boxShadow: `0 0 0 3px ${S.blue}` }
  },
  // Animated fade out - starts after scroll completes
  highlightedItemFading: {
    animation: '$highlightFadeOut 600ms ease-out forwards',
    [DARK]: { animation: '$highlightFadeOutDark 600ms ease-out forwards' }
  },
  schema: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 2,
    '&.is-child': {
      marginBottom: '15px',
      '&:last-child': {
        marginBottom: '0'
      }
    },
  },
  title: {
    marginBottom: '5px',
    fontWeight: '500',
    fontSize: '24px',
    padding: '0px 10px 10px 10px',
  },
  title2: {
    marginBottom: '20px',
    fontWeight: '500',
    fontSize: '18px',
    padding: '0px 10px 10px 0px',
    color: 'hsl(0, 0%, 40%)',
    borderBottom: '1px solid hsl(0, 0%, 90%)',
    [DARK]: { color: S.muted, borderBottomColor: S.line }
  },
  label: {
    fontSize: '12px',
    fontWeight: '400',
    paddingRight: '10px',
    paddingBottom: '6px',
    color: 'hsl(0, 0%, 40%)',
    padding: '0 0px 5px 3px',
    [DARK]: { color: S.muted }
  },
  textInput: {
    flexGrow: '2',
    marginTop: '0px',
    fontSize: '13px',
    fontWeight: '400',
    color: 'hsl(0, 0%, 40%)',
    [DARK]: { color: S.muted }
  },
  commonInput: {
    marginTop: '0px',
    paddingLeft: '20px',
    fontSize: '12px',
    fontWeight: '400',
    color: 'hsl(0, 0%, 40%)',
    [DARK]: { color: S.muted }
  },
  children: {
    marginLeft: '20px',
    marginBottom: '20px',
    border: '1px solid hsl(0, 0%, 90%)',
    padding: '10px 20px',
    background: 'none',
    borderRadius: '5px',
    '&.removeItem-enter': {
      opacity: '0.01'
    },
    '&.removeItem-enter&.removeItem-enter-active': {
      opacity: '1',
      transition: 'opacity 800ms ease-in'
    },
    '&.removeItem-leave': {
      opacity: '1'
    },
    '&.removeItem-leave&.removeItem-leave-active': {
      opacity: '0.01',
      transition: 'opacity 300ms ease-in'
    },
    [DARK]: { borderColor: S.line }
  },
  addItem: {
    display: 'flex',
    fontSize: '14px',
    color: 'hsl(0, 0%, 40%)',
    [DARK]: { color: S.muted },
    alignItems: 'center',
    fontWeight: '300',
    marginBottom: '10px',
    marginTop: '20px',
    cursor: 'pointer',
    paddingRight: '20px',
    alignSelf: 'flex-end',
    '& > .material-icons': {
      fontSize: '14px',
      marginRight: '5px'
    }
  },
  moveItem: {
    fontSize: '14px',
    fontWeight: '300',
    marginBottom: '20px',
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    color: 'hsl(0, 0%, 40%)',
    [DARK]: { color: S.muted },
    cursor: 'pointer',
    alignSelf: 'flex-end',
    '& > .material-icons': {
      fontSize: '14px',
      marginRight: '5px'
    }
  },
  moveBtn: {
    cursor: 'pointer',
    paddingRight: '20px',
    '& > .material-icons': {
      fontSize: '14px',
      marginRight: '5px'
    }
  },
  removeItem: {
    fontSize: '14px',
    color: 'hsl(0, 100%, 40%)',
    [DARK]: { color: S.red },
    fontWeight: '300',
    marginBottom: '20px',
    marginTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  removeBtn: {
    cursor: 'pointer',
    paddingRight: '20px',
    '& > .material-icons': {
      fontSize: '14px',
      marginRight: '5px'
    }
  },
  sketchPicker: {
    marginBottom: '25px',
  },
  // Inspector column (compact) variants.
  commonInputCompact: { paddingLeft: 0 },
  compactLabel: { padding: '0 0 5px', color: S.muted },
  compactRootLabel: { fontSize: '13px', fontWeight: 600, padding: '0 0 6px' },
  childrenCompact: { marginLeft: 0, marginBottom: '12px', padding: '6px 10px 10px', border: `1px solid ${S.line}`, borderRadius: '6px' },
  childrenFlat: { margin: 0, padding: 0, border: 0 },
  compactHeader: {
    all: 'unset',
    boxSizing: 'border-box',
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: S.fg,
    '& .material-icons': { fontSize: '18px', color: S.muted }
  },
  compactColor: { display: 'grid', gap: '8px', marginBottom: '12px', justifyItems: 'start' },
  compactSwatchButton: {
    all: 'unset',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px 4px 4px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: '6px',
    background: S.field
  },
  compactSwatch: { width: '20px', height: '20px', borderRadius: '4px', boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.15)' },
  compactColorValue: { fontFamily: S.mono, fontSize: '12px', color: S.fg },
  // Simple string array styles
  simpleArrayItem: {
    marginBottom: '10px',
    marginLeft: '20px',
    border: '1px solid hsl(0, 0%, 90%)',
    borderRadius: '5px',
    padding: '10px 12px',
    background: 'hsl(0, 0%, 98%)',
    [DARK]: { borderColor: S.line, background: S.chrome }
  },
  simpleArrayItemHeader: {
    marginBottom: '8px',
  },
  simpleArrayItemLabel: {
    fontSize: '12px',
    fontWeight: '500',
    color: 'hsl(0, 0%, 40%)',
    [DARK]: { color: S.muted }
  },
  simpleArrayItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  simpleArrayInput: {
    flex: 1,
    fontSize: '13px',
    fontWeight: '400',
    color: 'hsl(0, 0%, 40%)',
    [DARK]: { color: S.muted }
  },
  simpleArrayRemoveBtn: {
    cursor: 'pointer',
    color: 'hsl(0, 70%, 50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '4px',
    transition: 'background 0.2s',
    '&:hover': {
      background: 'hsl(0, 70%, 95%)',
    },
    '& .material-icons': {
      fontSize: '18px',
    },
    [DARK]: { color: S.red, '&:hover': { background: TONES.red.bg } }
  },
  infoBox: { background: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151', [DARK]: { background: S.chrome, borderColor: S.line, color: S.fg } }
};

export default injectSheet(styles)(EditComponentProp);
